import React from "react";

import { formatDateOnly, formatDateTime } from "../../../../utils/datetime";
import { show } from "../../tenancies/workspaceUi";
import {
  noticePeriodMonths,
  noticePeriodText,
  terminatedByLabel,
} from "./terminationModel";

/**
 * KND-2 — the read-only halves of the termination block.
 *
 * PRESENTATION ONLY: every value comes from the server's response (or, for the
 * notice-period fallback, the tenancy row already on the page). Nothing here
 * fetches, derives a date or judges one.
 *
 * ONE COLUMN ON A PHONE, TWO FROM `sm`. The workspace's `Pairs` is two columns
 * at every width, and its `wide` cell is a plain `col-span-2` — inside a
 * one-column grid that opens an implicit second track and pushes the row wider
 * than the card. So the grid is local, and a wide cell only spans from `sm`.
 * The typography is `Field`'s, unchanged.
 */

const dateOrNull = (iso) => (iso ? formatDateOnly(iso) : null);
const instantOrNull = (iso) => (iso ? formatDateTime(iso) : null);

function Item({ label, value, wide = false, testId }) {
  return (
    <div className={wide ? "min-w-0 sm:col-span-2" : "min-w-0"} data-testid={testId}>
      <dt className="text-[11.5px] leading-tight text-muted-foreground">
        {label}
      </dt>
      <dd className="m-0 mt-[2px] whitespace-pre-line break-words text-[13px] text-foreground">
        {show(value)}
      </dd>
    </div>
  );
}

function Grid({ children, testId }) {
  return (
    <dl
      className="m-0 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2"
      data-testid={testId}
    >
      {children}
    </dl>
  );
}

/** The active termination, as recorded. */
export function RecordedTerminationSummary({ response, tn }) {
  const record = response?.termination || null;
  return (
    <Grid testId="termination-summary">
      <Item
        label="Kündigung eingegangen am"
        value={dateOrNull(response?.notice_given_at)}
      />
      <Item
        label="Ende des Mietverhältnisses"
        value={dateOrNull(response?.termination_effective_date)}
      />
      <Item
        label="Kündigungsfrist"
        value={noticePeriodText(noticePeriodMonths(record, tn))}
      />
      <Item
        label="Spätestes Check-out"
        value={instantOrNull(record?.latest_checkout_at)}
      />
      <Item
        label="Abnahme/Übergabetermin"
        value={instantOrNull(record?.handover_at)}
      />
      <Item label="Übergabeort" value={record?.handover_location} />
      <Item label="Kündigungsgrund" value={record?.termination_reason} wide />
      <Item label="Notiz" value={record?.note} wide />
    </Grid>
  );
}

/** Termination signals written outside this workflow (the old edit form). */
export function LegacyTerminationSummary({ response }) {
  return (
    <Grid testId="termination-legacy-summary">
      <Item
        label="Kündigung eingegangen am"
        value={dateOrNull(response?.notice_given_at)}
      />
      <Item
        label="Ende des Mietverhältnisses"
        value={dateOrNull(response?.termination_effective_date)}
      />
      <Item
        label="Gekündigt durch"
        value={terminatedByLabel(response?.terminated_by)}
      />
    </Grid>
  );
}

/** When the record was taken back — the act itself stays in the Verlauf. */
export function withdrawnAtText(response) {
  const at = response?.termination?.withdrawn_at;
  return at ? `Zurückgenommen am ${formatDateTime(at)}.` : null;
}
